// Shared receipt body for an `OrderSnapshot` — the item list + subtotal/tax/
// discount/total breakdown rendered by BOTH `ReceiptModal` (inside its
// `ModalContainer`, with its own "Your items" header above) and
// `FeedbackInbox/RightRail/OrderInfoPanel` (bare, below its own Date/Location
// sections). Extracted rather than left duplicated across the two — see
// review finding on chassis PR 07's `OrderInfoPanel`, which had copied this
// ~150-line block wholesale.
import { Text } from '@thanx/uikit/text'
import type { OrderSnapshot, OrderSnapshotItem } from 'api/recoveryAi.types'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { currencyFormatter } from 'utilities/formatters'

const t = buildTranslate('components.order_snapshot_receipt')

export interface OrderSnapshotReceiptProps {
  order: OrderSnapshot
  // The only rendered difference between the two callers: `ReceiptModal`'s
  // item list sits below its own "Your items" header (a 16px gap, 8px top
  // margin matches that header's rhythm); the rail's `OrderInfoPanel` has no
  // such header (its own Date/Location sections above already carry the
  // vertical rhythm), so it passes a tighter 12px gap and no top margin.
  itemsGap?: string
  itemsMarginTop?: string
}

// "Fries (2)" when there's more than one, plain "Fries" for qty 1.
const itemLabel = (item: OrderSnapshotItem): string =>
  item.quantity > 1 ? `${item.name} (${item.quantity})` : item.name

// priceCents is per-unit (nexus's OrderSnapshot::Serializer contract, so
// qty * price reconciles with subtotalCents) — the row must show the line
// total, not the per-unit price, or a qty-3 row displays 1/3 of what it cost.
const lineTotalCents = (item: OrderSnapshotItem): number | undefined =>
  item.priceCents != null ? item.priceCents * item.quantity : undefined

const OrderSnapshotReceipt = ({
  order,
  itemsGap = '16px',
  itemsMarginTop = '8px',
}: OrderSnapshotReceiptProps) => {
  const [css] = useStyletron()

  // The totals strip sits below the line-item list as a <dl> so screen
  // readers associate each label with its value (WCAG 1.3.1) rather than
  // reading loose sequential text. Each row is a dt/dd pair; Promo and Tax
  // are conditional (producer-blocked nil today — DATA-4857), rendered only
  // when present. The items list below reuses the same dt/dd shape for the
  // same reason.
  const rowClass = css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  })
  const totalRowClass = css({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
  })
  // dt/dd carry default browser margins (dd indents ~40px) — reset so the
  // flex space-between row controls layout, not the UA stylesheet.
  const termClass = css({ margin: 0 })
  const itemsListClass = css({
    display: 'flex',
    flexDirection: 'column',
    gap: itemsGap,
    marginTop: itemsMarginTop,
    marginBottom: 0,
  })
  const dividerClass = css({
    height: '1px',
    backgroundColor: palette.grey30,
    margin: '16px 0',
  })

  // Zero-amount discounts carry no information for the operator — filter
  // them out before they factor into a row OR the promo-suppression rule
  // below.
  const visibleDiscounts = (order.discounts ?? []).filter(
    discount => discount.amountCents > 0
  )
  // Nexus emits `discounts[]` (which already folds in the promo) alongside
  // the legacy `promo` string — suppress the Promo row so the same discount
  // isn't shown twice.
  const showPromo =
    typeof order.promo === 'string' &&
    order.promo.length > 0 &&
    visibleDiscounts.length === 0
  const totalLabel =
    order.orderTotalCents != null
      ? currencyFormatter(order.orderTotalCents / 100)
      : t('value_unavailable')

  return (
    <>
      <dl className={itemsListClass}>
        {order.items.map((item, index) => {
          const lineTotal = lineTotalCents(item)
          return (
            // Order items have no stable id on the snapshot, so disambiguate
            // same-name rows with the index alongside the name.
            <div className={rowClass} key={`${index}-${item.name}`}>
              <dt className={termClass}>
                <Text.BodyText4>{itemLabel(item)}</Text.BodyText4>
                {item.modifier && (
                  <Text.BodyText5 tag="div" color="grey70">
                    {item.modifier}
                  </Text.BodyText5>
                )}
              </dt>
              <dd className={termClass}>
                <Text.BodyText4>
                  {lineTotal != null
                    ? currencyFormatter(lineTotal / 100)
                    : t('value_unavailable')}
                </Text.BodyText4>
              </dd>
            </div>
          )
        })}
      </dl>

      <div className={dividerClass} />

      <dl className={css({ margin: 0 })}>
        {order.subtotalCents != null && (
          <div className={totalRowClass}>
            <dt className={termClass}>
              <Text.BodyText4 color="grey70">{t('subtotal')}</Text.BodyText4>
            </dt>
            <dd className={termClass}>
              <Text.BodyText4 color="grey70">
                {currencyFormatter(order.subtotalCents / 100)}
              </Text.BodyText4>
            </dd>
          </div>
        )}
        {order.taxCents != null && (
          <div className={totalRowClass}>
            <dt className={termClass}>
              <Text.BodyText4 color="grey70">{t('tax')}</Text.BodyText4>
            </dt>
            <dd className={termClass}>
              <Text.BodyText4 color="grey70">
                {currencyFormatter(order.taxCents / 100)}
              </Text.BodyText4>
            </dd>
          </div>
        )}
        {visibleDiscounts.map((discount, index) => (
          // Discounts have no stable id on the snapshot, so disambiguate
          // same-label rows with the index alongside the label (mirrors the
          // line-items key above).
          <div className={totalRowClass} key={`${index}-${discount.label}`}>
            <dt className={termClass}>
              <Text.BodyText4 color={palette.nephritis}>
                {discount.label}
              </Text.BodyText4>
            </dt>
            <dd className={termClass}>
              <Text.BodyText4 color={palette.nephritis}>
                {currencyFormatter(-discount.amountCents / 100)}
              </Text.BodyText4>
            </dd>
          </div>
        ))}
        {showPromo && (
          <div className={totalRowClass}>
            <dt className={termClass}>
              <Text.BodyText4 color="grey70">{t('promo')}</Text.BodyText4>
            </dt>
            <dd className={termClass}>
              <Text.BodyText4 color="grey70">{order.promo}</Text.BodyText4>
            </dd>
          </div>
        )}
        <div className={totalRowClass}>
          <dt className={termClass}>
            <Text.BodyText4 bold>{t('total')}</Text.BodyText4>
          </dt>
          <dd className={termClass}>
            <Text.BodyText4 bold>{totalLabel}</Text.BodyText4>
          </dd>
        </div>
      </dl>
    </>
  )
}

export default OrderSnapshotReceipt
